import {
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import {
  StickyNote,
  Plus,
  Search,
  Pencil,
  Trash2,
  Lock,
  X,
} from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { PrimaryButton as FieldPrimary } from '../../components/ui/Field';
import { AnnotationForm } from '../../components/forms/AnnotationForm';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { annotationApi } from '../../services/api-services';
import type { Annotation } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// AnnotationsPage — Operations Deck redesign (Variation A)
//
// Layout follows Companies / Locations / Users / Wells:
//   • 4-card stats strip (Total · In active use · Recently added · Locked)
//   • Search box (with × clear) + record count
//   • CSS-grid header row with SortHeader cells (server-side sort)
//   • Annotation rows — chip prefix, dim long-description, In-use lock pill
//   • Pagination footer
//   • AnnotationForm modal (read-only when annotation is in use)
// ─────────────────────────────────────────────────────────────

// Short · Long · Category · In use · Actions (mirrors variation-a-annotations)
const COL_TEMPLATE =
  'minmax(180px, 1.1fr) minmax(260px, 2.4fr) 130px 90px 90px';

type CategoryKey = 'core' | 'op' | 'test';
type CategoryFilter = 'all' | CategoryKey;

const CATEGORY_META: Record<CategoryKey, { label: string; color: string }> = {
  core: { label: 'Core',        color: 'var(--color-accent)' },
  op:   { label: 'Operational', color: '#7c6ba8' },
  test: { label: 'Test',        color: 'var(--color-warn)' },
};

// Heuristic classification — the Annotation entity has no Category
// column on the backend, but the Variation A design surfaces a
// Core / Operational / Test taxonomy. Until that field lands, we
// derive the bucket from the description text so the UI stays
// useful and matches the design language.
function classify(a: Annotation): CategoryKey {
  const s = (a.shortDescription || '').toLowerCase();
  const l = (a.longDescription || '').toLowerCase();
  if (s.includes('test') || s.startsWith('xyz') || /\btest\b/.test(l)) return 'test';
  if (/flow check|wait on cement|make-?up|pit volume|operation/i.test(l)) return 'op';
  return 'core';
}

export function AnnotationsPage() {
  const authUser = useAuthStore((s) => s.user);
  const canEdit = authUser?.role === 'SuperAdmin' || authUser?.role === 'CompanyAdmin';
  const username = authUser?.username;
  const recordActivity = useActivityStore((s) => s.record);

  const crud = useCrudPage<Annotation>({
    fetchFn: annotationApi.getAll,
    initialSortCol: 'ShortDescription',
  });
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Annotations + derived category, then filter by category chip.
  const annotationsWithCategory = useMemo(
    () => crud.data.map((a) => ({ ...a, _cat: classify(a) as CategoryKey })),
    [crud.data],
  );
  const filteredData = useMemo(() => {
    if (categoryFilter === 'all') return annotationsWithCategory;
    return annotationsWithCategory.filter((a) => a._cat === categoryFilter);
  }, [annotationsWithCategory, categoryFilter]);

  // Stats — per-page derivations + DB total.
  const stats = useMemo(() => {
    const onPage = crud.data;
    const inUse = onPage.filter((a) => a.isInUse).length;
    // Sum of usage indicators — until WellAnnotation count is exposed
    // per annotation, this is the closest proxy we have for "linked to wells".
    const linked = inUse;
    return {
      total: crud.totalCount,
      inUse,
      linked,
    };
  }, [crud.data, crud.totalCount]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  // CRUD wiring
  const handleSave = async (form: Partial<Annotation>) => {
    setSaving(true);
    try {
      const label = form.shortDescription?.trim() || 'Untitled annotation';
      const res = crud.editingItem
        ? await annotationApi.update(form)
        : await annotationApi.create(form);
      if (res?.status === 'Failed' || res?.error) {
        crud.showToast(res?.error?.errorMessage || 'Failed to save annotation', 'error');
        return;
      }
      crud.showToast(crud.editingItem ? 'Annotation updated' : 'Annotation created');
      recordActivity({
        type: crud.editingItem ? 'update' : 'create',
        entity: 'Annotations',
        label,
        user: username,
      });
      crud.closeModal();
      crud.load();
    } catch {
      crud.showToast('Failed to save annotation', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: Annotation) => {
    // Client-side guard — server enforces this too.
    if (a.isInUse) {
      crud.showToast(
        'This annotation is in use by one or more wells and cannot be deleted.',
        'error',
      );
      return;
    }
    if (!confirm(`Delete annotation "${a.shortDescription}"?`)) return;
    try {
      const res = await annotationApi.delete(a.id);
      if (res?.status === 'Failed' || res?.error) {
        crud.showToast(res?.error?.errorMessage || 'Failed to delete annotation', 'error');
        return;
      }
      crud.showToast('Annotation deleted');
      recordActivity({
        type: 'delete',
        entity: 'Annotations',
        label: a.shortDescription?.trim() || `Annotation #${a.id}`,
        user: username,
      });
      crud.load();
    } catch {
      crud.showToast('Failed to delete annotation', 'error');
    }
  };

  // TopBar primary action.
  usePageAction(
    canEdit ? (
      <FieldPrimary onClick={crud.openCreate}>
        <Plus size={14} /> New annotation
      </FieldPrimary>
    ) : null,
    [canEdit],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Hero + stats strip (1.6fr / 1fr / 1fr / 1fr — matches design) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
          gap: 16,
        }}
      >
        {/* Hero card */}
        <div
          style={{
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 10,
              background: 'rgba(11,74,111,0.10)',
              color: 'var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <StickyNote size={24} />
          </div>
          <div>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 22,
                fontWeight: 500,
                letterSpacing: -0.4,
                color: 'var(--color-text)',
                lineHeight: 1.1,
              }}
            >
              Annotation library
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--color-text-mid)',
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Reusable depth markers and operational events drilled crews log
              against wells and 3D models.
            </div>
          </div>
        </div>

        <NumberCard label="Total annotations" value={stats.total} loading={crud.loading} />
        <NumberCard label="In active use" value={stats.inUse} loading={crud.loading} />
        <NumberCard label="Linked to wells" value={stats.linked} loading={crud.loading} />
      </div>

      {/* Filter / search bar + grid */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 320,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search annotations…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Category chips — All / Core / Operational / Test */}
          <CategoryChips value={categoryFilter} onChange={setCategoryFilter} />

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : categoryFilter !== 'all'
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          <SortHeader
            label="Short description"
            col="ShortDescription"
            activeCol={crud.sortCol}
            dir={crud.sortDir}
            onClick={handleSort}
          />
          <SortHeader
            label="Long description"
            col="LongDescription"
            activeCol={crud.sortCol}
            dir={crud.sortDir}
            onClick={handleSort}
          />
          <div>Category</div>
          <div>In use</div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {crud.loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
            Loading…
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <StickyNote size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {crud.searchText
                ? 'No annotations match your search'
                : categoryFilter !== 'all'
                  ? `No annotations in “${CATEGORY_META[categoryFilter].label}”`
                  : 'No annotations yet'}
            </div>
            <div style={{ fontSize: 12 }}>
              {crud.searchText || categoryFilter !== 'all'
                ? 'Try a different keyword or clear the filter.'
                : canEdit
                  ? 'Add an annotation to the library to get started.'
                  : 'Annotations defined by an administrator will appear here.'}
            </div>
          </div>
        ) : (
          <div>
            {filteredData.map((a, i) => (
              <AnnotationRow
                key={a.id ?? i}
                annotation={a}
                category={a._cat}
                isLast={i === filteredData.length - 1}
                canEdit={canEdit}
                onEdit={() => crud.openEdit(a)}
                onDelete={() => handleDelete(a)}
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>
            {categoryFilter !== 'all' && filteredData.length !== crud.data.length && (
              <> (of {crud.data.length} on page)</>
            )}{' '}
            of{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span>{' '}
            annotation{crud.totalCount === 1 ? '' : 's'}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
              <PaginationButton
                onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))}
                disabled={crud.offset === 0}
              >
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton
                onClick={() => crud.setOffset(crud.offset + crud.pageSize)}
                disabled={currentPage >= totalPages}
              >
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <AnnotationForm
        open={crud.modalOpen}
        editing={crud.editingItem}
        saving={saving}
        onCancel={crud.closeModal}
        onSave={handleSave}
      />

      {crud.toast && (
        <Toast
          message={crud.toast.message}
          type={crud.toast.type}
          onClose={() => crud.setToast(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Row + helpers
// ─────────────────────────────────────────────────────────────

function AnnotationRow({
  annotation: a,
  category,
  isLast,
  canEdit,
  onEdit,
  onDelete,
}: {
  annotation: Annotation;
  category: CategoryKey;
  isLast: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const cat = CATEGORY_META[category];
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: COL_TEMPLATE,
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 13,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      {/* Short description as mono chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span
          style={{
            display: 'inline-block',
            minWidth: 36,
            padding: '3px 9px',
            borderRadius: 5,
            background: 'rgba(11,74,111,0.10)',
            color: 'var(--color-accent)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11.5,
            fontWeight: 600,
            textAlign: 'center',
            letterSpacing: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
          title={a.shortDescription ?? ''}
        >
          {a.shortDescription || '—'}
        </span>
      </div>

      {/* Long description */}
      <Cell dim ellipsis title={a.longDescription ?? ''}>
        {a.longDescription || '—'}
      </Cell>

      {/* Category pill — design colours: Core (accent), Operational (purple), Test (warn) */}
      <div>
        <span
          style={{
            display: 'inline-block',
            padding: '3px 9px',
            borderRadius: 4,
            border: `1px solid ${cat.color}33`,
            background: 'transparent',
            color: cat.color,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 0.2,
          }}
        >
          {cat.label}
        </span>
      </div>

      {/* In use — binary indicator (API doesn't expose count yet) */}
      <div>
        {a.isInUse ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: 'var(--font-mono)',
              fontSize: 11.5,
              color: 'var(--color-warn)',
              fontWeight: 500,
            }}
            title="Linked to one or more wells"
          >
            <Lock size={10} />
            Yes
          </span>
        ) : (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--color-text-dim)' }}>
            —
          </span>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        {canEdit && (
          <>
            <IconButton title={a.isInUse ? 'View (read-only)' : 'Edit'} onClick={onEdit}>
              <Pencil size={15} />
            </IconButton>
            <IconButton
              title={a.isInUse ? 'In use — delete blocked' : 'Delete'}
              onClick={onDelete}
              hoverDanger
            >
              <Trash2 size={15} />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}

// ── NumberCard — slim numeric stat tile (matches the three smaller
// cards in the design's hero strip).
function NumberCard({
  label,
  value,
  loading,
}: {
  label: string;
  value: number;
  loading?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        padding: '20px 22px',
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-dim)',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 32,
          fontWeight: 500,
          letterSpacing: -0.8,
          color: 'var(--color-text)',
          marginTop: 4,
          lineHeight: 1,
          opacity: loading ? 0.4 : 1,
          transition: 'opacity 180ms',
        }}
      >
        {loading ? '—' : String(value).padStart(2, '0')}
      </div>
    </div>
  );
}

// ── CategoryChips — All / Core / Operational / Test segmented control.
// Matches the four chip pills in the design's filter bar.
function CategoryChips({
  value,
  onChange,
}: {
  value: CategoryFilter;
  onChange: (v: CategoryFilter) => void;
}) {
  const opts: { v: CategoryFilter; label: string; dot?: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'core', label: 'Core', dot: CATEGORY_META.core.color },
    { v: 'op', label: 'Operational', dot: CATEGORY_META.op.color },
    { v: 'test', label: 'Test', dot: CATEGORY_META.test.color },
  ];
  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: active ? 500 : 400,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              background: active ? 'var(--color-accent)' : 'transparent',
              color: active ? '#fff' : 'var(--color-text-mid)',
              border: active ? 'none' : '1px solid var(--color-border)',
              transition: 'all 120ms',
            }}
          >
            {o.dot && !active && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: o.dot,
                  display: 'inline-block',
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Cell({
  children,
  mono = false,
  dim = false,
  ellipsis = false,
  style,
  title,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  ellipsis?: boolean;
  style?: CSSProperties;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: dim ? 'var(--color-text-mid)' : 'var(--color-text)',
        fontSize: mono ? 13 : 14,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const idleBg = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';
  const active = hover;
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${active ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: active ? hoverBg : idleBg,
        color: active ? hoverColor : idleColor,
        transition: 'all 120ms',
        transform: active ? 'translateY(-1px)' : 'none',
        boxShadow: active ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
